import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetMany } from "react-admin";
import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { ResourceName, type Vendor, type VendorRunout } from "@/types";

interface PolicyVendorRunoutInputsProps {
  /** Display label, e.g. "TPA" or "Rx". */
  label: string;
  /** Form field holding the selected vendor IDs (membership). */
  vendorIdsSource: "tpa_vendor_ids" | "rx_vendor_ids";
  /** Form field holding the per-vendor run-out entries. */
  runoutSource: "tpa_vendor_runout" | "rx_vendor_runout";
}

/**
 * Per-vendor run-out role inputs (#5325) for the policy create/edit form. Watches
 * the selected vendor IDs and, for each, offers a Run-out checkbox + (when checked)
 * a cutover date. Only run-out entries are written into form state — a vendor absent
 * from the run-out array defaults to primary (matches the backend contract).
 */
export const PolicyVendorRunoutInputs = ({ label, vendorIdsSource, runoutSource }: PolicyVendorRunoutInputsProps) => {
  const { setValue } = useFormContext();
  const ids = (useWatch({ name: vendorIdsSource }) as string[] | undefined) ?? [];
  const runout = (useWatch({ name: runoutSource }) as VendorRunout[] | undefined) ?? [];

  const { data: vendors = [] } = useGetMany<Vendor>(
    ResourceName.VENDORS,
    { ids, meta: { "X-Fields": "id,company_name" } },
    { enabled: ids.length > 0 }
  );
  const nameById = useMemo(() => new Map(vendors.map((v) => [String(v.id), v.company_name])), [vendors]);
  const byId = useMemo(() => new Map(runout.map((r) => [r.vendor_id, r])), [runout]);

  if (ids.length === 0) return null;

  // Rebuild the run-out array from current ids + the edited vendor, keeping only
  // run-out entries (primary vendors are simply omitted).
  const commit = (vendorId: string, patch: Partial<VendorRunout>) => {
    const next: VendorRunout[] = [];
    for (const id of ids) {
      const cur = byId.get(id) ?? { vendor_id: id, is_runout: false };
      const merged = id === vendorId ? { ...cur, ...patch } : cur;
      if (merged.is_runout) next.push(merged);
    }
    setValue(runoutSource, next, { shouldDirty: true });
  };

  return (
    <Box sx={{ mt: 0.5, mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label} run-out administrators
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {ids.map((id) => {
          const entry = byId.get(id);
          const isRunout = entry?.is_runout ?? false;
          return (
            <Stack key={id} direction="row" spacing={1.5} alignItems="center">
              <FormControlLabel
                sx={{ minWidth: 260, m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={isRunout}
                    onChange={(e) => commit(id, { is_runout: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="body2">
                    {nameById.get(id) ?? "…"}{" "}
                    <Typography component="span" variant="caption" color="text.disabled">
                      run-out
                    </Typography>
                  </Typography>
                }
              />
              {isRunout && (
                <TextField
                  type="date"
                  size="small"
                  label="Cutover date"
                  InputLabelProps={{ shrink: true }}
                  value={entry?.runout_cutover_date ?? ""}
                  onChange={(e) => commit(id, { is_runout: true, runout_cutover_date: e.target.value || undefined })}
                />
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};
